/**
 * Stage Summary Adapters — Standard Mode
 *
 * Each adapter takes the raw executor result object and returns a normalized
 * summary shape for the StageSummaryPanel to render.
 *
 * Return shape:
 *   {
 *     metrics:  Array<{ label, value, sub?, badge? }>
 *     findings: Array<{ title, body?, rows?: Array<{label, value, bold?}>, tags?: string[] }>
 *   }
 *
 * Rules:
 *   - NEVER return fake/demo values — only real data from result
 *   - Always guard every field access (result may be partial or null)
 *   - Return null if result is empty — callers will show a fallback
 */

// ─── Shared helpers ───────────────────────────────────────────────────────────

function fmt(n, fallback = '—') {
  if (n == null || n === '') return fallback
  if (typeof n === 'number') return n.toLocaleString()
  return String(n)
}

function pct(n, alreadyPercent = true) {
  if (n == null) return '—'
  const v = alreadyPercent ? n : n * 100
  return Math.round(v) + '%'
}

function dur(ms) {
  if (!ms || typeof ms !== 'number') return '—'
  if (ms < 1000)  return ms + ' ms'
  if (ms < 60000) return (ms / 1000).toFixed(1) + 's'
  return Math.round(ms / 60000) + 'm ' + Math.round((ms % 60000) / 1000) + 's'
}

/** Try several possible field names on obj, return first non-null. */
function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj?.[k] != null && obj[k] !== '') return obj[k]
  }
  return null
}

// ─── File-type helpers (shared with upload step) ─────────────────────────────

const FILE_TYPE_LABELS = {
  cobol_programs: 'COBOL Programs', cobol: 'COBOL Programs', programs: 'COBOL Programs',
  copybooks: 'Copybooks', copy: 'Copybooks', cpy: 'Copybooks',
  jcl: 'JCL', sql: 'SQL Scripts', config: 'Config Files',
  configuration: 'Config Files', properties: 'Properties', xml: 'XML Files',
  other: 'Other Files', unknown: 'Other Files',
}

function friendlyType(key) {
  const k = (key || '').toLowerCase().replace(/-/g, '_')
  return FILE_TYPE_LABELS[k] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

function extractFileTypes(result) {
  const catalog = result?.classified_catalog
  const summary = result?.summary

  if (catalog && typeof catalog === 'object' && !Array.isArray(catalog)) {
    const rows = Object.entries(catalog)
      .map(([k, v]) => ({ label: friendlyType(k), count: Array.isArray(v) ? v.length : (typeof v === 'number' ? v : v?.count ?? 0) }))
      .filter(r => r.count > 0)
      .sort((a, b) => b.count - a.count)
    if (rows.length > 0) return rows
  }

  if (summary && typeof summary === 'object' && !Array.isArray(summary)) {
    const rows = Object.entries(summary)
      .filter(([, v]) => typeof v === 'number' && v > 0)
      .map(([k, count]) => ({ label: friendlyType(k), count }))
      .sort((a, b) => b.count - a.count)
    if (rows.length > 0) return rows
  }

  return []
}

// ─── Adapter: upload (pipeline-upload) ───────────────────────────────────────

export function adaptUpload(result) {
  if (!result) return null

  const fileTypes = extractFileTypes(result)
  const total = result.file_count ?? fileTypes.reduce((s, t) => s + t.count, 0)

  const metrics = [
    {
      label: 'Files Extracted',
      value: fmt(total),
      sub: 'Total files from archive',
      badge: total > 0 ? 'complete' : 'pending',
    },
    {
      label: 'Archive Status',
      value: result.duplicate ? 'Reused' : 'Fresh Upload',
      sub: result.duplicate ? 'Reusing prior extract' : 'New archive processed',
      badge: result.duplicate ? 'moderate' : 'good',
    },
    result.source_hash && {
      label: 'Source Hash',
      value: result.source_hash.slice(0, 12) + '…',
      sub: 'Unique archive fingerprint',
      badge: 'scanned',
    },
  ].filter(Boolean)

  const findings = []

  if (fileTypes.length > 0) {
    const fileRows = fileTypes.map(t => ({ label: t.label, value: t.count.toLocaleString() }))
    fileRows.push({ label: 'Total', value: total.toLocaleString(), bold: true })
    findings.push({ title: 'File Breakdown', rows: fileRows })
  }

  if (total > 0) {
    findings.push({
      title: 'Ready for Analysis',
      body: 'All files have been extracted and inventoried. The full pipeline is now running — each stage below will unlock automatically as it completes.',
    })
  }

  if (result.artifacts_path) {
    findings.push({
      title: 'Storage Location',
      body: 'Extracted source files are stored in your secure S3 workspace and will be referenced by each subsequent analysis stage.',
    })
  }

  return { metrics, findings }
}

// ─── Adapter: code-analysis (pipeline-code-analysis) ─────────────────────────

export function adaptCodeAnalysis(result) {
  if (!result) return null
  const s = result.summary || {}

  const programCount = pick(s, 'total_programs', 'programs_analyzed', 'cobol_programs', 'program_count', 'programs')
  const fileCount    = pick(s, 'total_files', 'file_count', 'files_analyzed', 'total_source_files')
  const riskLevel    = pick(s, 'risk_level', 'overall_risk', 'risk', 'risk_category')
  const readiness    = pick(s, 'readiness_score', 'modernization_readiness', 'readiness', 'modernization_score')

  const metrics = [
    programCount != null && {
      label: 'Programs Found',
      value: fmt(programCount),
      sub: 'COBOL programs identified',
      badge: 'scanned',
    },
    fileCount != null && {
      label: 'Files Processed',
      value: fmt(fileCount),
      sub: 'Total source files',
      badge: 'scanned',
    },
    riskLevel != null && {
      label: 'Risk Level',
      value: typeof riskLevel === 'number' ? pct(riskLevel) : String(riskLevel),
      sub: 'Modernization risk',
      badge: typeof riskLevel === 'number' ? (riskLevel > 70 ? 'moderate' : 'good') : 'moderate',
    },
    readiness != null && {
      label: 'Readiness',
      value: typeof readiness === 'number' && readiness <= 1 ? pct(readiness * 100) : (typeof readiness === 'number' ? pct(readiness) : String(readiness)),
      sub: 'Transformation readiness',
      badge: 'good',
    },
    !programCount && !fileCount && result.main_program && {
      label: 'Main Program',
      value: result.main_program,
      sub: 'Primary entry point',
      badge: 'scanned',
    },
    !programCount && !fileCount && !readiness && result.duration_ms && {
      label: 'Analysis Time',
      value: dur(result.duration_ms),
      sub: 'Processing duration',
      badge: 'complete',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Code analysis finished', badge: 'complete' })
  }

  const findings = []

  // Key dependencies
  const keyDeps = pick(s, 'key_dependencies', 'dependencies', 'external_dependencies', 'copybook_dependencies')
  if (Array.isArray(keyDeps) && keyDeps.length > 0) {
    findings.push({
      title: 'Key Dependencies',
      tags: keyDeps.slice(0, 12).map(d => typeof d === 'string' ? d : d?.name || String(d)),
    })
  }

  // Major findings
  const majorFindings = pick(s, 'major_findings', 'findings', 'key_findings', 'analysis_findings')
  if (Array.isArray(majorFindings) && majorFindings.length > 0) {
    findings.push({
      title: 'Key Findings',
      body: majorFindings.slice(0, 5).map(f => typeof f === 'string' ? f : f?.description || String(f)).join('\n'),
    })
  }

  // Main program entry point
  if (result.main_program) {
    findings.push({
      title: 'Entry Point',
      body: `Main program identified as "${result.main_program}". This is the primary entry point used as the anchor for all downstream analysis.`,
    })
  }

  // Summarize what's available in reports — one card, not a list of filenames
  const hasArtifacts = result.artifacts?.json?.length > 0 || result.artifacts_path
  if (hasArtifacts) {
    findings.push({
      title: 'Full Report Available',
      body: 'Dependency graphs, risk assessment, transformation readiness scores, and the full code inventory are available in the Reports section.',
    })
  }

  if (findings.length === 0) {
    findings.push({
      title: 'Analysis Complete',
      body: 'Code analysis finished. Full dependency graphs, risk assessment, and transformation readiness details are available in the Reports section.',
    })
  }

  return { metrics, findings }
}

// ─── Adapter: ai-naming (pipeline-ai-naming) ──────────────────────────────────

export function adaptAiNaming(result) {
  if (!result) return null
  const s = result.summary || {}

  const renamed    = pick(s, 'renamed_count', 'total_renamed', 'identifiers_renamed', 'renamed_identifiers', 'total_identifiers_renamed')
  const totalIds   = pick(s, 'total_identifiers', 'total_count', 'total', 'identifier_count')
  const confidence = pick(s, 'confidence_score', 'confidence', 'avg_confidence', 'average_confidence', 'mean_confidence')
  const unresolved = pick(s, 'unresolved_count', 'unresolved', 'failed_count', 'errors', 'unresolved_identifiers')
  const domains    = pick(s, 'domains', 'groupings', 'naming_domains', 'categories', 'domain_groups')

  const confPct = confidence != null
    ? (typeof confidence === 'number' && confidence <= 1 ? pct(confidence * 100) : pct(confidence))
    : null

  const metrics = [
    renamed != null && {
      label: 'Identifiers Renamed',
      value: fmt(renamed),
      sub: totalIds != null ? `of ${fmt(totalIds)} total` : 'COBOL identifiers mapped',
      badge: 'complete',
    },
    confPct && {
      label: 'Confidence',
      value: confPct,
      sub: 'Average naming confidence',
      badge: confidence > 0.8 || confidence > 80 ? 'good' : 'moderate',
    },
    unresolved != null && {
      label: 'Unresolved',
      value: fmt(unresolved),
      sub: 'Identifiers needing review',
      badge: unresolved === 0 ? 'good' : 'moderate',
    },
    result.duration_ms && {
      label: 'Duration',
      value: dur(result.duration_ms),
      sub: 'AI naming time',
      badge: 'complete',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'AI naming finished', badge: 'complete' })
  }

  const findings = []

  if (Array.isArray(domains) && domains.length > 0) {
    findings.push({
      title: 'Naming Domains',
      tags: domains.slice(0, 14).map(d => typeof d === 'string' ? d : d?.name || String(d)),
    })
  }

  const topMappings = pick(s, 'top_mappings', 'sample_mappings', 'examples', 'mappings')
  if (Array.isArray(topMappings) && topMappings.length > 0) {
    findings.push({
      title: 'Sample Renames',
      rows: topMappings.slice(0, 6).map(m => ({
        label: m.original || m.cobol_name || m.from || '?',
        value: m.renamed || m.java_name || m.to || '?',
      })),
    })
  }

  findings.push({
    title: 'What This Stage Did',
    body: 'AI Naming translated cryptic COBOL identifiers (e.g. WS-CUST-ACC-BAL-01) into meaningful Java class and method names using domain recognition and semantic analysis.',
  })

  return { metrics, findings }
}

// ─── Adapter: code-refactor (pipeline-refactor) ───────────────────────────────

export function adaptCodeRefactor(result) {
  if (!result) return null
  const s = result.summary || {}

  const classesAnalyzed  = pick(s, 'total_classes_analyzed', 'classes_analyzed', 'classes', 'class_count')
  const patternsDetected = pick(s, 'total_patterns_detected', 'patterns_detected', 'patterns', 'pattern_count')
  const recommendations  = pick(s, 'total_recommendations', 'recommendations', 'recommendation_count')
  const highSeverity     = pick(s, 'total_high_severity', 'high_severity', 'critical_count', 'critical_issues')

  const metrics = [
    classesAnalyzed != null && {
      label: 'Classes Analyzed',
      value: fmt(classesAnalyzed),
      sub: 'Java classes reviewed',
      badge: 'scanned',
    },
    patternsDetected != null && {
      label: 'Patterns Detected',
      value: fmt(patternsDetected),
      sub: 'Code patterns found',
      badge: patternsDetected > 0 ? 'moderate' : 'good',
    },
    recommendations != null && {
      label: 'Recommendations',
      value: fmt(recommendations),
      sub: 'Refactoring actions',
      badge: 'scanned',
    },
    highSeverity != null && {
      label: 'High Severity',
      value: fmt(highSeverity),
      sub: 'Critical issues found',
      badge: highSeverity > 0 ? 'moderate' : 'good',
    },
    recommendations == null && result.recipes_count != null && {
      label: 'Recipes',
      value: fmt(result.recipes_count),
      sub: 'Transformation recipes',
      badge: 'complete',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Code refactor finished', badge: 'complete' })
  }

  const findings = []

  // Top refactoring recipes — show pattern names, not internal IDs
  if (Array.isArray(result.recipes) && result.recipes.length > 0) {
    const recipeNames = result.recipes
      .slice(0, 8)
      .map(r => r.name || r.recipe_name || r.type || r.pattern)
      .filter(Boolean)
    if (recipeNames.length > 0) {
      findings.push({ title: 'Refactoring Patterns Found', tags: recipeNames })
    }
  }

  // Class-level summary — show which classes had the most patterns
  if (Array.isArray(result.class_results) && result.class_results.length > 0) {
    const topClasses = result.class_results
      .filter(c => (c.patterns_count ?? c.recommendations_count ?? 0) > 0)
      .sort((a, b) => (b.patterns_count ?? 0) - (a.patterns_count ?? 0))
      .slice(0, 5)
    if (topClasses.length > 0) {
      findings.push({
        title: 'Highest Impact Classes',
        rows: topClasses.map(c => ({
          label: c.class_name || c.name || 'Class',
          value: c.patterns_count != null ? `${c.patterns_count} patterns` : `${c.recommendations_count} recs`,
        })),
      })
    }
  }

  findings.push({
    title: result.phase === 'analyze' ? 'Analysis Report Ready' : 'Transforms Applied',
    body: result.phase === 'analyze'
      ? 'Structural refactoring recommendations have been generated. These inform the Service Generation stage which applies the actual code transforms.'
      : 'Code analysis completed and structural transforms were applied to the Java codebase. Review the full report for change details.',
  })

  return { metrics, findings }
}

// ─── Adapter: dependency-mapper (pipeline-dependencies) ──────────────────────

export function adaptDependencyMapper(result) {
  if (!result) return null
  const s = result.summary || {}

  const nodeCount    = pick(s, 'node_count', 'total_nodes', 'nodes', 'program_count', 'component_count')
  const depCount     = pick(s, 'dependency_count', 'total_dependencies', 'dependencies', 'edge_count', 'edges')
  const externalCalls = pick(s, 'external_calls', 'external_dependencies', 'external_count', 'cics_calls', 'db2_calls', 'external_call_count')
  const riskLevel    = pick(s, 'risk_level', 'overall_risk', 'coupling_risk', 'risk_score')

  const metrics = [
    nodeCount != null && {
      label: 'Components',
      value: fmt(nodeCount),
      sub: 'Programs and modules mapped',
      badge: 'scanned',
    },
    depCount != null && {
      label: 'Dependencies',
      value: fmt(depCount),
      sub: 'Relationships identified',
      badge: 'scanned',
    },
    externalCalls != null && {
      label: 'External Calls',
      value: fmt(externalCalls),
      sub: 'External system interactions',
      badge: externalCalls > 0 ? 'moderate' : 'good',
    },
    riskLevel != null && {
      label: 'Coupling Risk',
      value: typeof riskLevel === 'number' ? pct(riskLevel) : String(riskLevel),
      sub: 'Inter-component coupling',
      badge: typeof riskLevel === 'number' ? (riskLevel > 70 ? 'moderate' : 'good') : 'moderate',
    },
    !nodeCount && !depCount && result.duration_ms && {
      label: 'Duration',
      value: dur(result.duration_ms),
      sub: 'Mapping time',
      badge: 'complete',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Dependency mapping finished', badge: 'complete' })
  }

  const findings = []

  const hotspots = pick(s, 'hotspots', 'high_coupling_nodes', 'coupling_hotspots', 'dependency_hotspots')
  if (Array.isArray(hotspots) && hotspots.length > 0) {
    findings.push({
      title: 'Dependency Hotspots',
      tags: hotspots.slice(0, 10).map(h => typeof h === 'string' ? h : h?.name || String(h)),
    })
  }

  const boundaries = pick(s, 'microservice_boundaries', 'boundaries', 'suggested_boundaries', 'boundary_count')
  if (boundaries != null) {
    const count = typeof boundaries === 'number' ? boundaries : Array.isArray(boundaries) ? boundaries.length : null
    if (count != null) {
      findings.push({
        title: 'Suggested Boundaries',
        body: `${count} potential microservice boundaries identified based on dependency clustering and coupling analysis.`,
      })
    }
  }

  findings.push({
    title: 'Full Analysis Available',
    body: 'Dependency graph, coupling metrics, risk assessment, microservice boundary recommendations, and impact analysis are all available in the Reports section.',
  })

  return { metrics, findings }
}

// ─── Adapter: monolith-identifier (pipeline-monolith) ────────────────────────

export function adaptMonolithIdentifier(result) {
  if (!result) return null
  const s = result.summary || {}

  const patternsFound   = pick(s, 'patterns_found', 'total_patterns', 'monolith_patterns', 'patterns_detected', 'detected_patterns')
  const modularityScore = pick(s, 'modularity_score', 'modularity', 'cohesion_score', 'modularization_score')
  const capabilityCount = pick(s, 'business_capability_count', 'capabilities_count', 'capability_count', 'total_capabilities')
  const decompositionRec = pick(s, 'decomposition_strategy', 'recommended_strategy', 'strategy', 'decomposition_recommendation')

  const modPct = modularityScore != null
    ? (typeof modularityScore === 'number' && modularityScore <= 1 ? pct(modularityScore * 100) : pct(modularityScore))
    : null

  const metrics = [
    patternsFound != null && {
      label: 'Monolith Patterns',
      value: fmt(patternsFound),
      sub: 'Anti-patterns detected',
      badge: patternsFound > 0 ? 'moderate' : 'good',
    },
    modPct && {
      label: 'Modularity Score',
      value: modPct,
      sub: 'Code cohesion rating',
      badge: (modularityScore > 0.6 || modularityScore > 60) ? 'good' : 'moderate',
    },
    capabilityCount != null && {
      label: 'Business Capabilities',
      value: fmt(capabilityCount),
      sub: 'Identified domains',
      badge: 'scanned',
    },
    result.duration_ms && {
      label: 'Duration',
      value: dur(result.duration_ms),
      sub: 'Processing time',
      badge: 'complete',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Monolith identification finished', badge: 'complete' })
  }

  const findings = []

  const capabilities = pick(s, 'business_capabilities', 'capabilities', 'domains', 'capability_list')
  if (Array.isArray(capabilities) && capabilities.length > 0) {
    findings.push({
      title: 'Business Capabilities',
      tags: capabilities.slice(0, 12).map(c => typeof c === 'string' ? c : c?.name || String(c)),
    })
  }

  if (typeof decompositionRec === 'string' && decompositionRec.length > 0) {
    findings.push({
      title: 'Recommended Strategy',
      body: decompositionRec,
    })
  }

  findings.push({
    title: 'Full Analysis Available',
    body: 'Anti-pattern inventory, modularity metrics, business capability map, and decomposition strategy recommendations are available in the Reports section.',
  })

  return { metrics, findings }
}

// ─── Adapter: data-analysis (pipeline-data) ───────────────────────────────────

export function adaptDataAnalysis(result) {
  if (!result) return null
  const s = result.summary || {}

  const structureCount  = pick(s, 'data_structure_count', 'structures_found', 'total_structures', 'structure_count')
  const entityCount     = pick(s, 'entity_count', 'entities_found', 'total_entities', 'entities', 'table_count')
  const copybookCount   = pick(s, 'copybook_count', 'copybooks_analyzed', 'total_copybooks', 'copybooks')
  const fieldCount      = pick(s, 'total_fields', 'field_count', 'fields', 'total_field_count')
  const normalizations  = pick(s, 'normalization_opportunities', 'normalizations', 'optimization_count', 'normalization_count')

  const metrics = [
    structureCount != null && {
      label: 'Data Structures',
      value: fmt(structureCount),
      sub: 'Structures identified',
      badge: 'scanned',
    },
    entityCount != null && {
      label: 'Logical Entities',
      value: fmt(entityCount),
      sub: 'Data entities mapped',
      badge: 'scanned',
    },
    copybookCount != null && {
      label: 'Copybooks',
      value: fmt(copybookCount),
      sub: 'COBOL copybooks analyzed',
      badge: 'scanned',
    },
    fieldCount != null && {
      label: 'Fields',
      value: fmt(fieldCount),
      sub: 'Data fields catalogued',
      badge: 'scanned',
    },
    !structureCount && !entityCount && result.duration_ms && {
      label: 'Duration',
      value: dur(result.duration_ms),
      sub: 'Processing time',
      badge: 'complete',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Data analysis finished', badge: 'complete' })
  }

  const findings = []

  findings.push({
    title: 'Full Analysis Available',
    body: 'Entity-relationship diagram, data lineage map, copybook analysis, hierarchical data structures, and AI-powered schema insights are all available in the Reports section.',
  })

  if (normalizations != null) {
    findings.push({
      title: 'Normalization Opportunities',
      body: `${fmt(normalizations)} normalization opportunities identified in the COBOL data structures for the Java target model.`,
    })
  }

  const schemaTypes = pick(s, 'schema_types', 'data_types', 'data_categories')
  if (Array.isArray(schemaTypes) && schemaTypes.length > 0) {
    findings.push({
      title: 'Data Categories',
      tags: schemaTypes.slice(0, 10).map(t => typeof t === 'string' ? t : String(t)),
    })
  }

  if (findings.length === 0) {
    findings.push({
      title: 'Analysis Complete',
      body: 'Data models, schemas, and copybook structures have been analyzed. ERD and data lineage reports are available in the Reports section.',
    })
  }

  return { metrics, findings }
}

// ─── Adapter: discovery (pipeline-discovery) ─────────────────────────────────

export function adaptDiscovery(result) {
  if (!result) return null
  const s        = result.summary || {}
  const exec     = (typeof s.executive_summary === 'object' ? s.executive_summary : null) || {}
  const roi      = (typeof s.roi_analysis === 'object' ? s.roi_analysis : null) || {}
  const roadmap  = (typeof s.migration_roadmap === 'object' ? s.migration_roadmap : null) || {}
  const bizProc  = (typeof s.business_processes === 'object' ? s.business_processes : null) || {}
  const integrations = (typeof s.integration_points === 'object' ? s.integration_points : null) || {}

  const bizProcCount    = pick(bizProc, 'count', 'total', 'process_count') ?? pick(s, 'business_process_count', 'processes_found')
  const integrationCount = pick(integrations, 'count', 'total', 'integration_count') ?? pick(s, 'integration_count', 'integrations_found')
  const roiEstimate     = pick(roi, 'roi_percentage', 'roi_estimate', 'total_roi', 'estimated_roi') ?? pick(s, 'roi_percentage', 'roi')
  const timeline        = pick(roadmap, 'total_duration', 'duration', 'timeline', 'estimated_duration') ?? pick(s, 'migration_duration', 'timeline')
  const complexity      = pick(exec, 'application_complexity', 'complexity') ?? pick(s, 'complexity', 'application_complexity')

  const roiDisplay = roiEstimate != null
    ? (typeof roiEstimate === 'number'
        ? (roiEstimate <= 1 ? pct(roiEstimate * 100) : Math.round(roiEstimate) + '%')
        : String(roiEstimate))
    : null

  const metrics = [
    bizProcCount != null && {
      label: 'Business Processes',
      value: fmt(bizProcCount),
      sub: 'Processes identified',
      badge: 'scanned',
    },
    integrationCount != null && {
      label: 'Integration Points',
      value: fmt(integrationCount),
      sub: 'External integrations',
      badge: 'scanned',
    },
    roiDisplay && {
      label: 'Estimated ROI',
      value: roiDisplay,
      sub: 'Return on investment',
      badge: 'good',
    },
    timeline != null && {
      label: 'Migration Timeline',
      value: String(timeline),
      sub: 'Estimated duration',
      badge: 'moderate',
    },
    !bizProcCount && complexity != null && {
      label: 'Complexity',
      value: String(complexity),
      sub: 'Application complexity',
      badge: 'moderate',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Discovery finished', badge: 'complete' })
  }

  const findings = []

  const apiPatterns = typeof s.api_patterns === 'object' && s.api_patterns
  const apiCount = apiPatterns ? pick(apiPatterns, 'count', 'total', 'api_count') : null
  if (apiCount != null) {
    findings.push({
      title: 'API Patterns',
      body: `${fmt(apiCount)} API patterns identified in the application for integration design.`,
    })
  }

  const phases = pick(roadmap, 'phases', 'migration_phases', 'milestones')
  if (Array.isArray(phases) && phases.length > 0) {
    findings.push({
      title: 'Migration Roadmap',
      rows: phases.slice(0, 5).map((p, i) => ({
        label: p.name || p.phase || p.title || `Phase ${i + 1}`,
        value: p.duration || p.effort || p.weeks || '',
      })),
    })
  }

  const savings = pick(roi, 'cost_savings', 'annual_savings', 'savings', 'total_savings')
  if (savings != null) {
    findings.push({
      title: 'Cost Analysis',
      body: `Estimated annual savings: ${typeof savings === 'number' ? '$' + savings.toLocaleString() : savings}`,
    })
  }

  if (findings.length === 0) {
    findings.push({
      title: 'Business Analysis Complete',
      body: 'Executive summary, integration inventory, ROI analysis, and migration roadmap are all available in the Reports section.',
    })
  }

  return { metrics, findings }
}

// ─── Adapter: architecture (pipeline-architecture) ───────────────────────────

export function adaptArchitecture(result) {
  if (!result) return null
  const rec    = result.recommendations || {}
  const cost   = result.cost_estimate   || {}
  const phases = result.migration_phases || []

  const monthly = pick(cost, 'monthly_total', 'monthly', 'monthly_estimate', 'monthly_cost')
  const annual  = pick(cost, 'annual_total', 'annual', 'annual_estimate', 'annual_cost')

  const monthlyDisplay = monthly != null
    ? (typeof monthly === 'number' ? '$' + Math.round(monthly).toLocaleString() + '/mo' : String(monthly))
    : null
  const annualDisplay  = annual != null
    ? (typeof annual === 'number' ? '$' + Math.round(annual).toLocaleString() + '/yr' : String(annual))
    : null

  const warningCount = result.warnings?.length ?? null

  const metrics = [
    phases.length > 0 && {
      label: 'Migration Phases',
      value: fmt(phases.length),
      sub: 'Phased migration plan',
      badge: 'scanned',
    },
    monthlyDisplay && {
      label: 'Est. Monthly Cost',
      value: monthlyDisplay,
      sub: 'AWS infrastructure spend',
      badge: 'scanned',
    },
    annualDisplay && !monthlyDisplay && {
      label: 'Est. Annual Cost',
      value: annualDisplay,
      sub: 'AWS infrastructure spend',
      badge: 'scanned',
    },
    result.sources_used?.length > 0 && {
      label: 'Analysis Sources',
      value: fmt(result.sources_used.length),
      sub: 'Input data streams used',
      badge: 'complete',
    },
    warningCount != null && {
      label: 'Review Items',
      value: fmt(warningCount),
      sub: 'Architecture warnings',
      badge: warningCount > 0 ? 'moderate' : 'good',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Architecture design finished', badge: 'complete' })
  }

  const findings = []

  const computeRec = rec.compute || {}
  const computeServices = Object.keys(computeRec).filter(k => computeRec[k])
  if (computeServices.length > 0) {
    findings.push({ title: 'Compute Recommendations', tags: computeServices })
  }

  const dbRec = rec.database || {}
  const dbServices = Object.keys(dbRec).filter(k => dbRec[k])
  if (dbServices.length > 0) {
    findings.push({ title: 'Database Recommendations', tags: dbServices })
  }

  if (phases.length > 0) {
    findings.push({
      title: 'Migration Phases',
      rows: phases.slice(0, 5).map((p, i) => ({
        label: p.name || p.phase || `Phase ${i + 1}`,
        value: p.duration || p.effort || '',
      })),
    })
  }

  if (result.validation_report) {
    const vr = result.validation_report
    findings.push({
      title: 'Architecture Validated',
      body: (typeof vr === 'string' ? vr : vr?.summary || vr?.message) || 'Architecture validated against AWS Well-Architected Framework.',
    })
  }

  if (result.warnings?.length > 0) {
    findings.push({
      title: 'Warnings to Review',
      body: result.warnings.slice(0, 3).join('\n'),
    })
  }

  if (findings.length === 0) {
    findings.push({
      title: 'Architecture Ready',
      body: 'Target AWS architecture designed. CDK infrastructure templates, cost projections, and migration phasing are available in the Reports section.',
    })
  }

  return { metrics, findings }
}

// ─── Adapter: service-gen (pipeline-service-gen) ─────────────────────────────

export function adaptServiceGen(result) {
  if (!result) return null
  const s = result.summary || {}

  const totalServices  = pick(s, 'total_services', 'services_count', 'services', 'service_count')
  const totalMethods   = pick(s, 'total_methods', 'methods_count', 'methods', 'method_count')
  const methodsPerSvc  = pick(s, 'methods_per_service', 'avg_methods_per_service', 'methods_avg')
  const valPassed      = pick(s, 'validation_passed', 'validation_ok') ?? result.validation?.passed

  const metrics = [
    totalServices != null && {
      label: 'Services Generated',
      value: fmt(totalServices),
      sub: 'Java microservices created',
      badge: 'complete',
    },
    totalMethods != null && {
      label: 'Methods Generated',
      value: fmt(totalMethods),
      sub: 'Service method implementations',
      badge: 'complete',
    },
    methodsPerSvc != null && {
      label: 'Methods / Service',
      value: typeof methodsPerSvc === 'number' ? methodsPerSvc.toFixed(1) : fmt(methodsPerSvc),
      sub: 'Average per service',
      badge: 'scanned',
    },
    valPassed != null && {
      label: 'Validation',
      value: valPassed ? 'Passed' : 'Issues Found',
      sub: 'Code validation result',
      badge: valPassed ? 'good' : 'moderate',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Service generation finished', badge: 'complete' })
  }

  const findings = []

  if (Array.isArray(result.services) && result.services.length > 0) {
    findings.push({
      title: `Generated Services (${result.services.length})`,
      rows: result.services.slice(0, 8).map(svc => ({
        label: svc.name || svc.service_name || svc.class_name || 'Service',
        value: svc.method_count != null ? `${svc.method_count} methods` : svc.methods?.length != null ? `${svc.methods.length} methods` : '',
      })),
    })
  }

  if (result.validation) {
    const v = result.validation
    const errors = v.errors || []
    findings.push({
      title: 'Validation Results',
      body: v.passed
        ? `All validation checks passed.${errors.length === 0 ? '' : ` ${errors.length} item(s) noted.`}`
        : `Validation found ${errors.length} issue(s). Review the full report for details.`,
    })
  }

  if (result.artifacts?.services_dir) {
    findings.push({
      title: 'Output Location',
      body: 'Generated Java @Service classes are stored in your S3 workspace, ready for Pre-Flight compilation.',
    })
  }

  if (findings.length === 0) {
    findings.push({
      title: 'Services Ready',
      body: 'Java microservices generated from the analyzed COBOL programs. Proceed to Pre-Flight to compile and validate the output.',
    })
  }

  return { metrics, findings }
}

// ─── Adapter: pre-flight (pipeline-pre-flight) ────────────────────────────────

export function adaptPreFlight(result) {
  if (!result) return null

  const passed       = result.compile_passed
  const errorCount   = result.errors ?? 0
  const filesChecked = result.java_files_checked ?? 0

  const metrics = [
    {
      label: 'Compile Status',
      value: passed === true ? 'Passed' : passed === false ? 'Failed' : '—',
      sub: 'Maven compile result',
      badge: passed === true ? 'good' : passed === false ? 'moderate' : 'pending',
    },
    filesChecked > 0 && {
      label: 'Files Compiled',
      value: fmt(filesChecked),
      sub: 'Java files checked',
      badge: 'scanned',
    },
    {
      label: 'Compile Errors',
      value: fmt(errorCount),
      sub: errorCount === 0 ? 'Clean compile' : 'Issues found',
      badge: errorCount === 0 ? 'good' : 'moderate',
    },
  ].filter(Boolean)

  const findings = []

  if (passed === true) {
    findings.push({
      title: 'Compilation Successful',
      body: `${filesChecked > 0 ? filesChecked + ' Java ' : 'All Java '}files compiled without errors. The codebase is validated and ready for packaging.`,
    })
  } else if (passed === false) {
    findings.push({
      title: 'Compilation Issues Found',
      body: `${errorCount} compile error(s) detected. Pre-Flight automatically applies COBOL→Java fixes (OCCURS arrays, MOVE groups, string initializations). Review the detailed error report for any remaining manual fixes.`,
    })
  }

  findings.push({
    title: 'What Pre-Flight Validates',
    body: 'Runs Maven compile on the generated Java project and auto-applies proven COBOL-to-Java translation fixes before reporting results.',
  })

  return { metrics, findings }
}

// ─── Adapter: java-packages (pipeline-java-package) ──────────────────────────

export function adaptJavaPackages(result) {
  if (!result) return null
  const pkg = result.package_info || {}

  const metrics = [
    pkg.name && {
      label: 'Package Name',
      value: pkg.name,
      sub: 'Maven artifact ID',
      badge: 'complete',
    },
    pkg.version && {
      label: 'Version',
      value: pkg.version,
      sub: 'Package version',
      badge: 'complete',
    },
    result.file_count != null && {
      label: 'Files Generated',
      value: fmt(result.file_count),
      sub: 'Output files in package',
      badge: 'scanned',
    },
    {
      label: 'Docker',
      value: result.docker_included ? 'Included' : 'Not Included',
      sub: result.docker_included ? 'Dockerfile generated' : 'No Docker output',
      badge: result.docker_included ? 'good' : 'pending',
    },
    result.validation_passed != null && {
      label: 'Validation',
      value: result.validation_passed ? 'Passed' : 'Failed',
      sub: 'Package validation',
      badge: result.validation_passed ? 'good' : 'moderate',
    },
  ].filter(Boolean).slice(0, 4)

  if (metrics.length === 0) {
    metrics.push({ label: 'Status', value: 'Complete', sub: 'Java packaging finished', badge: 'complete' })
  }

  const findings = []

  if (pkg.spring_boot_version || pkg.java_version || pkg.name) {
    findings.push({
      title: 'Package Configuration',
      rows: [
        pkg.name             && { label: 'Artifact ID',    value: pkg.name },
        pkg.version          && { label: 'Version',         value: pkg.version },
        pkg.spring_boot_version && { label: 'Spring Boot', value: pkg.spring_boot_version },
        pkg.java_version     && { label: 'Java Version',   value: pkg.java_version },
      ].filter(Boolean),
    })
  }

  if (Array.isArray(result.files_generated) && result.files_generated.length > 0) {
    findings.push({
      title: 'Generated Files',
      rows: result.files_generated.slice(0, 6).map(f => ({ label: f.split('/').pop() || f, value: '' })),
    })
  }

  if (result.validation_errors?.length > 0) {
    findings.push({
      title: 'Validation Notes',
      body: result.validation_errors.slice(0, 3).join('\n'),
    })
  }

  findings.push({
    title: result.download_url ? 'Package Ready for Download' : 'Package Complete',
    body: result.download_url
      ? 'Your modernized Java application is packaged and ready for download. Access it through the Reports section.'
      : 'Your COBOL application has been successfully modernized to a deployable Spring Boot Java package.',
  })

  return { metrics, findings }
}

// ─── Adapter registry ─────────────────────────────────────────────────────────

const STAGE_ADAPTERS = {
  'upload':              adaptUpload,
  'code-analysis':       adaptCodeAnalysis,
  'ai-naming':           adaptAiNaming,
  'code-refactor':       adaptCodeRefactor,
  'dependency-mapper':   adaptDependencyMapper,
  'monolith-identifier': adaptMonolithIdentifier,
  'data-analysis':       adaptDataAnalysis,
  'discovery':           adaptDiscovery,
  'architecture':        adaptArchitecture,
  'service-gen':         adaptServiceGen,
  'pre-flight':          adaptPreFlight,
  'java-packages':       adaptJavaPackages,
}

/**
 * Get the summary data for a stage given its result object.
 * Returns null if no adapter found or result is empty.
 */
export function getStageSummary(stepId, result) {
  if (!result) return null
  const adapter = STAGE_ADAPTERS[stepId]
  if (!adapter) return null
  try {
    return adapter(result)
  } catch (e) {
    console.warn(`[StageSummary] Adapter error for "${stepId}":`, e)
    return null
  }
}
